import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { usePrefersReducedMotion } from "~/src/hooks/common/usePrefersReducedMotion";

const ExitContext = createContext({ exiting: false, complete: () => {} });

export const useModalExit = (): React.ContextType<typeof ExitContext> =>
  useContext(ExitContext);

/** Keep conditional feature owners mounted only until their dialog or sheet exits. */
export const ModalPresence = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const parentExit = useModalExit();
  const present = Boolean(children);
  const [retained, setRetained] = useState(children);
  const complete = useCallback(() => {
    setRetained(null);
  }, []);
  const prefersReducedMotion = usePrefersReducedMotion();
  const animate = !prefersReducedMotion;

  useEffect(() => {
    if (present) {
      setRetained(children);
      return;
    }
    if (!animate) {
      complete();
      return;
    }
    if (!retained) return;
    // Fallback for interrupted animations (resize, hidden tab, removed content).
    const timeout = window.setTimeout(complete, 250);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [children, present, animate, complete, retained]);

  const value = useMemo(
    () => ({
      exiting: parentExit.exiting || !present,
      complete: parentExit.exiting ? parentExit.complete : complete,
    }),
    [parentExit.exiting, parentExit.complete, present, complete]
  );

  return (
    <ExitContext.Provider value={value}>
      {present ? children : animate ? retained : null}
    </ExitContext.Provider>
  );
};
